import React, { FunctionComponent, useEffect, useState } from 'react'
import {
  Badge,
  Button,
  Icon,
  Link,
  LoaderSpinner,
  Popover,
  PopoverList,
  PopoverListItem,
  WithPopover,
} from '@lightspeed/design-system-react'
import { T, useT } from '@transifex/react'
import { CustomerBadge, MultiAvatar } from '@vend/react-utilities'
import { noWidow, trackUserEvent } from '@vend/utilities'
import IMAGES_CDN_PATH from '@vend/images-ui'
import classNames from 'classnames'
import { isValidCustomer } from '../../../../functions/sale'
import { useAppSelector } from '../../../../state/hooks'
import { selectTrainingModeEnabled } from '../../../../state/slices/posState/posStateSlice'
import { useWebRegisterStatusManager } from '../../../../ng-compat/hooks'
import { EVENTS } from '../../../../constants'
import { ROUTES } from '../../../../../../lib/routes'
import { WEB_REGISTER } from '../../../web-register.constant'
import { SaleSummary } from './generateSaleSummaries'
import { useParkedSales } from './useParkedSales'
import { NoSales } from './NoSales'

interface RetrieveSaleProps {
  retrieveParkedSale: (saleId: string) => void
}

const DEFAULT_IMAGE_PATH = `${IMAGES_CDN_PATH}products/placeholder.svg`

const MAX_PRODUCT_SUMMARY = 3

export const RetrieveSale: FunctionComponent<RetrieveSaleProps> = ({
  retrieveParkedSale,
}) => {
  const t = useT()
  const [active, setActive] = useState(false)
  const open = () => {
    setActive(true)
    trackUserEvent(WEB_REGISTER.DISPLAY_NAME, 'Retrieve Sale Popover Shown')
  }
  const close = () => setActive(false)
  const trainingModeEnabled = useAppSelector(selectTrainingModeEnabled)
  const webRegisterStatusManager = useWebRegisterStatusManager()

  const salesResult = useParkedSales(active)

  const isDisabled = trainingModeEnabled || !webRegisterStatusManager.isOnline()
  const itemSelected = (saleId: string) => {
    retrieveParkedSale(saleId)
    close()
  }

  useEffect(() => {
    const closeIfOpen = () => {
      if (active) {
        close()
      }
    }
    webRegisterStatusManager.on(EVENTS.statusChange, closeIfOpen)
    return () => {
      webRegisterStatusManager.off(EVENTS.statusChange, closeIfOpen)
    }
  }, [active, webRegisterStatusManager])

  const getSaleHeader = (sale: SaleSummary) => {
    const products =
      sale.products.length > MAX_PRODUCT_SUMMARY
        ? sale.products.slice(0, MAX_PRODUCT_SUMMARY - 1).concat({
            name: t('and {productCount} more', {
              productCount: sale.products.length - MAX_PRODUCT_SUMMARY + 1,
              _context: 'Count of additional products in parked sale list',
            }),
            productID: 'more-products',
          })
        : sale.products

    return products.length ? (
      <>
        {products.map(product => (
          <div
            key={product.productID}
            className="wr-retrieve-sale-product-summary"
          >
            {product.name === 'Product not found' ? (
              <T
                _str="Product not found"
                _context="Could not find the product when listing parked sales"
              />
            ) : (
              product.name
            )}
          </div>
        ))}
      </>
    ) : (
      <div>
        <T
          _str="No Products"
          _context="Label when there are no products in a parked sale"
        />
      </div>
    )
  }

  const isLoading = !salesResult
  const noResult = salesResult && !salesResult.sales.length
  const hasResult = !isLoading && !noResult

  return (
    <WithPopover>
      <Button
        modifier="text"
        variant="supplementary"
        onClick={open}
        disabled={isDisabled}
        data-track="retrieve-sale"
      >
        <Icon icon="fa-share" className="vd-mr2" />
        <T
          _str="Retrieve Sale"
          _context="Button to open retrieve sale popover"
        />
      </Button>
      {active ? (
        <Popover
          onClose={close}
          className="vd-popover--with-list wr-retrieve-sale-popover"
        >
          {isLoading ? (
            <div className="vd-pa10 vd-align-center">
              <LoaderSpinner />
            </div>
          ) : null}
          {noResult ? <NoSales /> : null}
          {hasResult ? (
            <PopoverList>
              <li className="wr-retrieve-sale-header wr-retrieve-sale-item">
                <div className="vd-text-label wr-retrieve-sale-item-details">
                  <T
                    _str="Parked Sale"
                    _context="Column heading for parked sale list"
                  />
                </div>
                <div className="vd-text-label wr-retrieve-sale-item-customer">
                  <T
                    _str="Customer"
                    _context="Column heading for parked sale list"
                  />
                </div>
              </li>
              {salesResult.sales.map((sale, i) => (
                <PopoverListItem
                  index={i}
                  key={sale.id}
                  className="wr-retrieve-sale-item"
                  onItemSelected={() => itemSelected(sale.id)}
                >
                  <div
                    className={classNames(
                      'wr-retrieve-sale-item-details',
                      'vd-pt2',
                      'vd-mr4',
                      {
                        'wr-multi-image-badge': sale.products.length,
                      }
                    )}
                  >
                    <Badge
                      header={getSaleHeader(sale)}
                      description={noWidow(sale.parkedAt)}
                      avatar={
                        sale.products.length ? (
                          <MultiAvatar entities={sale.products} />
                        ) : undefined
                      }
                      image={
                        sale.products.length ? undefined : DEFAULT_IMAGE_PATH
                      }
                    />
                    {sale.note ? (
                      <div className="wr-retrieve-sale-item-note vd-text-supplementary">
                        <strong>
                          <T _str="Note:" _context="Label for sale not field" />
                        </strong>{' '}
                        {sale.note}
                      </div>
                    ) : null}
                  </div>
                  <div className="wr-retrieve-sale-item-customer-react">
                    {isValidCustomer(sale.customer) ? (
                      <CustomerBadge
                        size="x-small"
                        apiCustomer={sale.customer}
                      />
                    ) : (
                      <Badge header="-" />
                    )}
                  </div>
                  <Icon
                    icon="fa-share"
                    className="wr-retrieve-sale-item-icon-react"
                  />
                </PopoverListItem>
              ))}
              {salesResult.hasMore ? (
                <PopoverListItem className="vd-align-center" index={12}>
                  <Link
                    href={`${ROUTES.NEW_SALES_HISTORY_CONTINUE}?status=SAVED`}
                    onClick={() => {
                      trackUserEvent(
                        WEB_REGISTER.DISPLAY_NAME,
                        'Retrieve Sale Popover: Show All Parked Sales',
                        null,
                        { useSendBeacon: true }
                      )
                    }}
                    data-track="view-all-parked-sales"
                  >
                    <T
                      _str="View all parked sales"
                      _context="Show more parked sales"
                    />
                  </Link>
                </PopoverListItem>
              ) : null}
            </PopoverList>
          ) : null}
        </Popover>
      ) : null}
    </WithPopover>
  )
}
